import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Switch, 
  FormControlLabel, 
  Button, 
  Snackbar, 
  Alert,
  CircularProgress,
  Container,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/layout/PageHeader';
import * as notificationService from '../../services/notification';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationSettings as NotificationSettingsType } from '../../services/notification';

const NotificationSettings: React.FC = () => {
  const [settings, setSettings] = useState<NotificationSettingsType>({
    // 通知チャンネル
    email_notifications: false,
    line_notifications: false,
    
    // 通知内容
    order_notifications: false,
    shift_change_notifications: false,
    
    // 以下はAPI互換性のために残す（UI上では非表示）
    gps_tracking_notifications: false,
    system_notifications: false,
    app_notifications: false,
    sms_notifications: false,
    push_notifications: false,
    daily_summary: false,
    weekly_summary: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await notificationService.getNotificationSettings();
        setSettings(data);
      } catch (err) {
        setError('通知設定の取得に失敗しました');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSettingChange = async (setting: string, value: boolean) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [setting]: value
    }));

    try {
      await notificationService.updateNotificationSettings({ [setting]: value });
      setSuccess('設定が更新されました');
    } catch (err) {
      setError('設定の更新に失敗しました');
      console.error(err);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await notificationService.updateNotificationSettings(settings);
      setSuccess('通知設定を更新しました');
    } catch (err) {
      setError('設定の保存に失敗しました');
      console.error(err);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {error && (
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
          >
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Snackbar>
        )}

        {success && (
          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            onClose={() => setSuccess(null)}
          >
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          </Snackbar>
        )}

        <Grid container spacing={3}>
          {/* 通知チャンネル設定 */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  通知の種類
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.email_notifications}
                          onChange={(e) => handleSettingChange('email_notifications', e.target.checked)}
                        />
                      }
                      label="メール通知"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                      ユーザーに設定されているメールアドレス宛に通知を送信します
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.line_notifications}
                          onChange={(e) => handleSettingChange('line_notifications', e.target.checked)}
                        />
                      }
                      label="LINE通知"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                      ユーザーに設定されているLINE ID宛に通知を送信します
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* 通知内容設定 */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  通知内容
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.order_notifications}
                          onChange={(e) => handleSettingChange('order_notifications', e.target.checked)}
                          disabled={!settings.email_notifications && !settings.line_notifications}
                        />
                      }
                      label="オーダー通知"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                      新規オーダーが作成されたときに、オーダー作成画面で選択された「キャスト」「ドライバー」宛に通知を送信します
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.shift_change_notifications}
                          onChange={(e) => handleSettingChange('shift_change_notifications', e.target.checked)}
                          disabled={!settings.email_notifications && !settings.line_notifications}
                        />
                      }
                      label="シフト通知"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                      シフトが登録された際に「管理者」「マネージャー」「内勤スタッフ」「ドライバー」のユーザーに通知を送信します
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* 保存ボタン */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSettings}
          >
            設定を保存
          </Button>
        </Box>
      </Container>
    );
  };

  return (
    <DashboardLayout title="通知設定">
      <PageHeader
        title="通知設定"
        subtitle="メールとLINE通知の設定を管理します"
      />
      {renderContent()}
    </DashboardLayout>
  );
};

export default NotificationSettings;